'use client';

import React, { ComponentProps, ForwardedRef, forwardRef } from 'react';

import MuiLink from '@mui/material/Link';

import NextLink from 'next/link';
import { NavLink } from 'react-router';

// This approach is used to avoid bundling unnecessary code
// when building both Vite and Next.js apps.
const ComponentLink =
  process.env.VITE_APP_ENV !== 'true' || process.env.NEXT_PUBLIC_APP_ENV === 'nextjs'
    ? NextLink
    : NavLink;

/**
 * This is a MUI wrapper over `next/link` component.
 * We use this to help us maintain consistency between Vite and Next.js
 */
export const Link = forwardRef(
  (
    {
      href,
      legacyBehavior,
      passHref,
      prefetch,
      rel,
      scroll,
      target,
      ...props
    }: ComponentProps<typeof MuiLink> & {
      component?: React.ElementType;
      legacyBehavior?: boolean;
      passHref?: boolean;
      prefetch?: boolean;
      scroll?: boolean;
    },
    ref: ForwardedRef<HTMLAnchorElement>,
  ) => {
    const isOutsideLink =
      href &&
      href.startsWith('http') &&
      !new URL(href).hostname.endsWith('cloudquery.io') &&
      (process.env.VERCEL_ENV !== 'preview' || !new URL(href).hostname.endsWith('vercel.app'));
    const parsedRel = rel ?? (isOutsideLink ? 'noopener noreferrer nofollow' : undefined);
    const parsedTarget = target ?? (isOutsideLink ? '_blank' : undefined);
    const hrefProps =
      process.env.NEXT_PUBLIC_APP_ENV === 'nextjs'
        ? { href, legacyBehavior, passHref, prefetch, scroll }
        : { to: href };

    return (
      <MuiLink
        ref={ref}
        component={ComponentLink}
        data-noscroll={scroll === false ? 'true' : undefined}
        rel={parsedRel}
        target={parsedTarget}
        {...hrefProps}
        {...props}
      />
    );
  },
);
